import { Button, Dialog, Stack, breakpoints, vars } from '@etg/wings';
import { ReactComponent as RevolutPassengersIcon } from '@eti/assets/icons/revolut/profile.svg';
import { useRevolut, useProperty, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { constants } from '@eti/utils';
import { CaretDown, User } from '@phosphor-icons/react';
import type { MutableRefObject, ReactNode } from 'react';
import { forwardRef, useEffect, useReducer, useRef } from 'react';
import FocusTrap from 'react-focus-lock';
import { observeClickOutside } from 'react-ref-observe';
import { useMediaQuery } from 'usehooks-ts';

const { REVOLUT } = constants.brands;

const wrapperStyles = css`
  display: flex;
  flex: 1;
  position: relative;

  &::before {
    border: 0.1875rem solid ${vars.colors.inputs.focus};
    border-radius: 0.375rem;
    content: '';
    height: calc(100% + 0.375rem);
    left: -0.1875rem;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: -0.1875rem;
    transition: opacity 0.2s ease-in-out;
    width: calc(100% + 0.375rem);
  }

  &:focus-within {
    z-index: 1;
  }

  &:focus-within::before {
    opacity: 1;
  }
`;

const triggerStyles = css`
  align-items: center;
  appearance: none;
  background-color: ${vars.colors.inputs.background};
  border-color: ${vars.colors.inputs.border};
  border-radius: ${vars.inputs.borderRadius};
  border-style: solid;
  border-width: 0.0625rem;
  color: inherit;
  display: flex;
  font-family: inherit;
  font-size: 1rem;
  height: ${vars.inputs.height};
  justify-content: space-between;
  outline: none;
  padding-inline: ${vars.inputs.paddingInline};
  position: relative;
  text-align: start;
  width: 100%;

  &[disabled] {
    background-color: ${vars.colors.inputs.disabledBackground};
    border-color: ${vars.colors.inputs.disabledBorder};
    cursor: not-allowed;
  }

  &:focus {
    border-color: ${vars.colors.inputs.borderFocused};
  }

  [data-inputgroup] & {
    border-radius: 0;
  }

  [data-inputgroup] *:not(:last-child) & {
    border-inline-end: none;
    padding-inline-end: 0.625rem;
  }

  [data-inputgroup] &:focus {
    border-inline-end: 0.0625rem solid ${vars.colors.inputs.borderFocused};
    border-radius: 0.1875rem;
  }

  [data-inputgroup] > *:first-child & {
    border-end-start-radius: ${vars.inputs.borderRadius};
    border-start-start-radius: ${vars.inputs.borderRadius};
  }

  [data-inputgroup] > *:last-child & {
    border-end-end-radius: 0.1875rem;
    border-start-end-radius: 0.1875rem;
  }

  [data-inputgroup] *:not(:last-child) &:focus {
    padding-inline-end: 0.5625rem;
  }
`;

const triggerWithIconStyles = css`
  cursor: pointer;
  padding-inline: ${vars.inputs.floatingLabel.paddingInlineStartWithIcon}
    ${vars.inputs.iconInsetMargin};
  width: 96px;
`;

const dialogStyles = css`
  & > * > :nth-child(2) {
    min-height: 390px;
  }

  /* ensures the footer ”confirm” button sticks to the bottom of the dialog. */
  & > * > :last-child {
    margin-top: auto;
  }
`;

const desktopListWrapper = css`
  background-color: ${vars.colors.surface.primary};
  border: 0.0625rem solid ${vars.colors.selectBox.listBox.desktopBorder};
  border-radius: ${vars.inputs.borderRadius};
  inset-inline: 0;
  max-height: 21.25rem;
  overflow: auto;
  padding: ${vars.selectBox.option.margin};
  position: absolute;
  top: calc(100% + ${vars.selectBox.listBox.desktopDistance});
  width: 100%;
  z-index: 9;
`;

const labelStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const getCaretWrapperStyles = (isOpened: boolean) => css`
  display: flex;
  margin-inline-start: 0.5rem;
  position: relative;

  &::before {
    background-color: ${vars.colors.inputs.focus};
    border-radius: 0.1875rem;
    content: '';
    height: calc(100% + 0.0625rem);
    opacity: ${isOpened ? '1' : '0'};
    position: absolute;
    top: -0.0625rem;
    transition: opacity 0.4s ease-in-out;
    width: 100%;
  }
`;

const getCaretStyles = (isOpened: boolean) => css`
  flex-shrink: 0;
  height: 1.25rem;
  transform: rotateX(${isOpened ? '180deg' : '0deg'});
  transition: transform 0.15s ease-in-out;
  width: 1.25rem;
`;

const passengerIconStyles = css`
  height: auto;
  inset-inline-start: ${vars.inputs.iconInsetMargin};
  pointer-events: none;
  position: absolute;
  top: calc(((${vars.inputs.height} - 2px) - ${vars.inputs.iconSize}) / 2);
  width: ${vars.inputs.iconSize};
`;

const passengerIconsPerBrand = {
  [REVOLUT]: RevolutPassengersIcon,
  default: User,
};

const iconColorsPerBrand = {
  [REVOLUT]: vars.colors.text.secondary,
  default: 'currentColor',
};

interface DesktopOptionsWrapperProps {
  children: ReactNode;
  close: () => void;
  id: string;
  wrapperRef: MutableRefObject<HTMLElement | null>;
}

const DesktopOptionsWrapper = ({ children, close, id, wrapperRef }: DesktopOptionsWrapperProps) => {
  observeClickOutside([wrapperRef], close);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyPress, false);
    return () => {
      document.removeEventListener('keydown', handleKeyPress, false);
    };
  }, [close]);

  return (
    <div className={desktopListWrapper} id={id}>
      {children}
    </div>
  );
};

export interface PassengersToggleProps {
  buttonId?: string;
  children: ReactNode;
  childrenAgesLabel?: ReactNode;
  isDisabled?: boolean;
  label: ReactNode;
  labelledBy?: string;
  passengersCount: number;
  title: string;
}

const PassengersToggle = forwardRef<HTMLButtonElement, PassengersToggleProps>(
  function PassengersToggle(
    {
      buttonId,
      children,
      childrenAgesLabel,
      isDisabled,
      label,
      labelledBy,
      passengersCount,
      title,
    },
    ref,
  ) {
    const { isRevolut } = useRevolut();
    const { t } = useTranslation();
    const { p } = useProperty();
    const [opened, toggleOpened] = useReducer((previous) => !previous, false);
    const wrapperRef = useRef(null);
    const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);

    const useCondensedTrigger = p('SearchForm.Passengers.CondensedTrigger.Enabled');
    const useAlternateLayout = p('SearchForm.Passengers.AlternateInputLayout.Enabled');
    const PassengerIcon = useCondensedTrigger
      ? passengerIconsPerBrand[isRevolut ? REVOLUT : 'default']
      : undefined;
    const passengersSelectionId = 'searchform-passengers-selection';
    const formattedChildrenAgesLabel = childrenAgesLabel && `. ${childrenAgesLabel}`;

    return (
      <div ref={wrapperRef}>
        <div className={wrapperStyles}>
          <button
            aria-controls={passengersSelectionId}
            aria-expanded={opened}
            aria-label={`${title}: ${label}${formattedChildrenAgesLabel}`}
            aria-labelledby={labelledBy}
            aria-live="assertive"
            className={cx(triggerStyles, useCondensedTrigger && triggerWithIconStyles)}
            data-testid="searchForm-passengers-dropdown"
            disabled={isDisabled}
            id={buttonId}
            onClick={toggleOpened}
            ref={ref}
            type="button"
          >
            {PassengerIcon && (
              <PassengerIcon
                aria-hidden
                className={passengerIconStyles}
                fill={iconColorsPerBrand[isRevolut ? REVOLUT : 'default']}
              />
            )}
            <span aria-atomic="true" aria-hidden className={labelStyles}>
              {useCondensedTrigger ? passengersCount : label}
            </span>
            {!useCondensedTrigger && (
              <span className={getCaretWrapperStyles(opened)}>
                <CaretDown aria-hidden className={getCaretStyles(opened)} weight="light" />
              </span>
            )}
          </button>
        </div>
        {isWiderViewport ? (
          opened && (
            <DesktopOptionsWrapper
              close={toggleOpened}
              id={passengersSelectionId}
              wrapperRef={wrapperRef}
            >
              <FocusTrap returnFocus>{children}</FocusTrap>
            </DesktopOptionsWrapper>
          )
        ) : (
          <Dialog
            className={cx({ [dialogStyles]: !useAlternateLayout })}
            closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
            footer={
              <Stack>
                <Button data-testid="passengers-confirm" onClick={toggleOpened} variant="primary">
                  {t('Searchform.Passengers.Dialog.Confirm.Label')}
                </Button>
              </Stack>
            }
            id={passengersSelectionId}
            isOpen={opened}
            onDismiss={toggleOpened}
            title={title}
          >
            {children}
          </Dialog>
        )}
      </div>
    );
  },
);

export default PassengersToggle;
